import DoubleSpreadDocument from '@/components/app/document/double-spread-document'
import {
  DOC_GRID_MAJOR_LINE_OFFSET_PT,
  DocumentGridOverlay,
} from '@/components/app/document/overlays/document-grid-overlay'
import SinglePageDocument from '@/components/app/document/single-page-document'
import { SpreadBackgroundFills } from '@/components/app/backgrounds/spread-background-layer'
import { isSingleFirstPage, resolveSinglePageSpreadSide } from '../helpers/spread-geometry'
import type { CanvasViewport } from '../types/canvas-groups'
import type { ResolvedCenterCanvasConfig } from '../types/resolved-center-canvas-config'

/**
 * The document grid, clipped to the trim rect (bleedMargin inset on every side of the bleed box
 * it is drawn in) so it stops at the dashed trim line instead of spilling into the bleed margin
 * outside it — same `clip-path: inset()` technique used elsewhere in this tree (see
 * print-bleed-tint.tsx), which stays reliable inside `documentCanvasRef`'s `transform: scale()`
 * ancestor unlike the legacy `clip: rect()` it replaced. Clipping via a wrapper here, rather than
 * adjusting `DocumentGridOverlay`'s own left/width, leaves its delicate pattern-phase math
 * (aligned to the ruler "0" tick and spine seam) completely untouched. A major line is anchored
 * at `phaseAnchorX` and at the top trim edge.
 */
function TrimClippedGridOverlay({
  mode,
  width,
  height,
  bleedMargin,
  phaseAnchorX,
}: {
  mode: 'full' | 'half'
  width: number
  height: number
  bleedMargin: number
  phaseAnchorX: number
}) {
  return (
    <div
      className="absolute inset-0 pointer-events-none"
      style={{
        // `clip-path` on this wrapper creates a NEW stacking context (same
        // category of gotcha as `transform` elsewhere in this tree) — without
        // an explicit z-index here, the wrapper is compared against its own
        // siblings as `z-index: auto`, so `.doc-safe-area-band` (z-index 6)
        // would always paint over the whole wrapper regardless of the
        // grid's own internal z-index (10). Matching that same 10 here lets the
        // wrapper win that comparison, so the grid is actually visible under
        // the margin bands instead of getting painted over by them.
        zIndex: 10,
        clipPath: `inset(${bleedMargin}px ${bleedMargin}px ${bleedMargin}px ${bleedMargin}px)`,
      }}
    >
      <DocumentGridOverlay
        left={0}
        top={0}
        width={width}
        height={height}
        mode={mode}
        phaseOffsetX={phaseAnchorX - DOC_GRID_MAJOR_LINE_OFFSET_PT}
        phaseOffsetY={bleedMargin - DOC_GRID_MAJOR_LINE_OFFSET_PT}
      />
    </div>
  )
}

/** Document: single page for first/last, double spread otherwise — with its background and grid. */
export function SpreadDocument({
  config,
  viewport,
}: {
  config: Pick<
    ResolvedCenterCanvasConfig,
    | 'pageViewMode'
    | 'gridMode'
    | 'bleedWidth'
    | 'bleedHeight'
    | 'trimWidth'
    | 'trimHeight'
    | 'bleedMargin'
    | 'liveInset'
    | 'centerGap'
    | 'marginInside'
    | 'marginOutside'
    | 'marginTop'
    | 'marginBottom'
    | 'columns'
    | 'rows'
    | 'colGutter'
    | 'rowGutter'
    | 'showGuides'
    | 'showLeftPageGuides'
    | 'showRightPageGuides'
    | 'showBleed'
  >
  viewport: Pick<
    CanvasViewport,
    | 'isSinglePage'
    | 'spreadBackgroundGeometry'
    | 'pageTrimWidth'
    | 'singlePageBleedWidth'
    | 'scale'
    | 'snappedSpreadOriginCanvasX'
  >
}) {
  const {
    pageViewMode,
    gridMode,
    bleedWidth,
    bleedHeight,
    trimWidth,
    trimHeight,
    bleedMargin,
    liveInset,
    centerGap,
    marginInside,
    marginOutside,
    marginTop,
    marginBottom,
    columns,
    rows,
    colGutter,
    rowGutter,
    showGuides,
    showLeftPageGuides,
    showRightPageGuides,
    showBleed,
  } = config
  const {
    isSinglePage,
    spreadBackgroundGeometry,
    pageTrimWidth,
    singlePageBleedWidth,
    scale,
    snappedSpreadOriginCanvasX,
  } = viewport
  const spreadBackgroundFills = <SpreadBackgroundFills {...spreadBackgroundGeometry} />

  return isSinglePage ? (
    <SinglePageDocument
      pageBackground={spreadBackgroundFills}
      bleedWidth={bleedWidth}
      bleedHeight={bleedHeight}
      trimWidth={pageTrimWidth}
      trimHeight={trimHeight}
      bleedMargin={bleedMargin}
      side={resolveSinglePageSpreadSide(pageViewMode)}
      marginLeft={isSingleFirstPage(pageViewMode) ? marginInside : marginOutside}
      marginRight={isSingleFirstPage(pageViewMode) ? marginOutside : marginInside}
      marginTop={marginTop}
      marginBottom={marginBottom}
      columns={columns}
      rows={rows}
      colGutter={colGutter}
      rowGutter={rowGutter}
      showGuides={showGuides}
      scale={scale}
      showPrintBleed={showBleed}
      showInnerTrimBleedFrame={!showBleed}
      tiledGridOverlay={(() => {
        if (gridMode !== 'full' && gridMode !== 'half') return null

        if (singlePageBleedWidth <= 0 || bleedHeight <= 0) return null

        // `tiledGridOverlay` renders inside `SinglePageDocument`'s `BleedFrame`, at
        // that frame's own local origin (left=0,top=0) — not full-canvas coords.
        // Anchor a major line at this page's own spine-side trim edge (local-left
        // for the first page, local-right for the last — see `SinglePageDocument`'s
        // own `side` doc comment) and at the top trim edge, via `phaseOffsetX/Y`
        // rather than shifting the overlay's own position, so the tile phase is
        // correct independent of this page's width.
        const singlePageSide = resolveSinglePageSpreadSide(pageViewMode)
        const spineAnchorXLocal =
          singlePageSide === 'right' ? bleedMargin : bleedMargin + pageTrimWidth

        return (
          <TrimClippedGridOverlay
            mode={gridMode}
            width={singlePageBleedWidth}
            height={bleedHeight}
            bleedMargin={bleedMargin}
            phaseAnchorX={spineAnchorXLocal}
          />
        )
      })()}
    />
  ) : (
    <DoubleSpreadDocument
      pageBackground={spreadBackgroundFills}
      bleedWidth={bleedWidth}
      bleedHeight={bleedHeight}
      trimWidth={trimWidth}
      trimHeight={trimHeight}
      bleedMargin={bleedMargin}
      liveInset={liveInset}
      marginInside={marginInside}
      marginOutside={marginOutside}
      marginTop={marginTop}
      marginBottom={marginBottom}
      columns={columns}
      rows={rows}
      colGutter={colGutter}
      rowGutter={rowGutter}
      scale={scale}
      showLeftPageGuides={showLeftPageGuides}
      showRightPageGuides={showRightPageGuides}
      centerGap={centerGap}
      showSpineGutter={showBleed && !isSinglePage}
      showPrintBleed={showBleed}
      showInnerTrimBleedFrame={!showBleed}
      spreadOriginSnapCanvasX={snappedSpreadOriginCanvasX}
      tiledGridOverlay={(() => {
        if (gridMode !== 'full' && gridMode !== 'half') {
          return null
        }

        const seamX = snappedSpreadOriginCanvasX

        if (seamX <= 0 || bleedHeight <= 0) {
          return null
        }

        // One continuous overlay (not split at the seam) anchored so a major line
        // lands exactly on the spine and another exactly on the top trim edge —
        // via `phaseOffsetX/Y` rather than shifting the overlay's own position.
        // Splitting into two SVGs (as this used to do) risked a sub-pixel seam at
        // the boundary between them (the exact class of bug already hit and fixed
        // for the gutter tint in `double-spread-gutter-tint-above-shapes.tsx`); one
        // continuous pattern anchored at the spine is mathematically identical to
        // "mirrored, tiling outward from the spine in both directions" for a
        // symmetric tile size, but can't develop a seam since there's only one SVG.
        return (
          <TrimClippedGridOverlay
            mode={gridMode}
            width={bleedWidth}
            height={bleedHeight}
            bleedMargin={bleedMargin}
            phaseAnchorX={seamX}
          />
        )
      })()}
    />
  )
}
